import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Camera, Heart, Baby, Upload, X, Loader2, Sparkles, AlertCircle, Check, Trash2, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Message format constants for chat widgets
export const PHOTO_MATCH_MESSAGE_TYPES = {
  REQUEST: 'PHOTO_MATCH_REQUEST',
  UPLOADED: 'PHOTO_MATCH_UPLOADED', 
  READY: 'PHOTO_MATCH_READY',
  RESULTS: 'PHOTO_MATCH_RESULTS'
};

// Helper to create photo match messages
export const createPhotoMatchMessage = (type, data) => {
  return `[${type}:${JSON.stringify(data)}]`;
};

// Helper to parse photo match messages
export const parsePhotoMatchMessage = (text) => {
  if (!text) return null;
  
  // Trim whitespace to handle any leading/trailing spaces or newlines
  const trimmedText = text.trim();
  
  for (const type of Object.values(PHOTO_MATCH_MESSAGE_TYPES)) {
    const prefix = `[${type}:`;
    if (trimmedText.startsWith(prefix) && trimmedText.endsWith(']')) {
      try {
        const jsonStr = trimmedText.slice(prefix.length, -1);
        const data = JSON.parse(jsonStr);
        return { type, data };
      } catch (e) {
        console.error('Failed to parse photo match message:', e);
        return null;
      }
    }
  }
  return null;
};

// Check if text is a photo match message (for quick checks)
export const isPhotoMatchMessage = (text) => {
  if (!text) return false;
  // Trim whitespace to handle any leading/trailing spaces or newlines
  const trimmedText = text.trim();
  return Object.values(PHOTO_MATCH_MESSAGE_TYPES).some(type => 
    trimmedText.startsWith(`[${type}:`) && trimmedText.endsWith(']')
  );
};

const PhotoMatchWidget = ({ 
  matchId, 
  currentUser, 
  otherUser, 
  onRefreshMessages,
  externalOpen,
  externalMode,
  onExternalClose
}) => {
  const [showModal, setShowModal] = useState(false);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingBaby, setGeneratingBaby] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [consentGiven, setConsentGiven] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);
  const fileInputRef = useRef(null);

  // Handle external open/close
  useEffect(() => {
    if (externalOpen) {
      setShowModal(true);
      if (externalMode === 'upload') {
        setUploadMode(true);
        setShowResults(false);
      } else if (externalMode === 'results') {
        setShowResults(true);
        setUploadMode(false);
      }
    }
  }, [externalOpen, externalMode]);

  const handleModalClose = (open) => {
    setShowModal(open);
    if (!open && onExternalClose) {
      onExternalClose();
    }
  };

  useEffect(() => {
    if (showModal && matchId) {
      loadSession();
    }
  }, [showModal, matchId]);

  const loadSession = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/photo-match/session/${matchId}`, {
        withCredentials: true
      });
      setSession(response.data.session);
      if (response.data.session?.compatibility_score) {
        setShowResults(true);
      }
    } catch (error) {
      console.error('Failed to load session:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendChatMessage = async (text) => {
    try {
      await axios.post(`${API}/matches/${matchId}/messages`, { text }, { withCredentials: true });
      if (onRefreshMessages) onRefreshMessages();
    } catch (error) {
      console.error('Failed to send chat message:', error);
    }
  };

  const startPhotoMatch = async () => {
    if (!consentGiven) {
      toast.error('Please accept the consent to continue');
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(`${API}/photo-match/request`, {
        partner_id: otherUser.id
      }, { withCredentials: true });
      
      setSession({ id: response.data.session_id, status: response.data.status, is_requester: true });
      setUploadMode(true);
      toast.success('Now upload your photos!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (files.length > 5) {
      toast.error('Maximum 5 photos allowed');
      return;
    }
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select only image files');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Each image must be less than 10MB');
        return;
      }
    }
    
    setUploading(true);
    const uploadedUrls = [];
    
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/upload/photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        });
        
        uploadedUrls.push(response.data.url);
      }
      
      setSelectedPhotos(uploadedUrls);
      toast.success(`${files.length} photo(s) ready!`);
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const submitPhotos = async () => {
    if (selectedPhotos.length === 0) {
      toast.error('Please select photos first');
      return;
    }
    
    try {
      setUploading(true);
      const response = await axios.post(`${API}/photo-match/upload/${session.id}`, {
        photo_urls: selectedPhotos
      }, { withCredentials: true });
      
      const newStatus = response.data.status;
      
      // Send appropriate chat widget message
      if (newStatus === 'both_uploaded') {
        // Both users have uploaded - send ready message
        const readyMessage = createPhotoMatchMessage(PHOTO_MATCH_MESSAGE_TYPES.READY, {
          sessionId: session.id,
          userName: currentUser.name
        });
        await sendChatMessage(readyMessage);
        toast.success('Both photos uploaded! Click to see results! 🎉');
      } else {
        // First user uploaded - send request message to partner
        const requestMessage = createPhotoMatchMessage(PHOTO_MATCH_MESSAGE_TYPES.UPLOADED, {
          sessionId: session.id,
          userName: currentUser.name,
          photoCount: selectedPhotos.length
        });
        await sendChatMessage(requestMessage);
        toast.success('Photos sent! Waiting for partner... 📸');
      }
      
      setSelectedPhotos([]);
      setUploadMode(false);
      loadSession();
      setShowModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit photos');
    } finally {
      setUploading(false);
    }
  };

  const runAnalysis = async () => {
    try {
      setAnalyzing(true);
      const response = await axios.post(`${API}/photo-match/analyze/${session.id}`, {}, {
        withCredentials: true,
        timeout: 120000
      });
      
      // Send results message to chat
      const resultsMessage = createPhotoMatchMessage(PHOTO_MATCH_MESSAGE_TYPES.RESULTS, {
        sessionId: session.id,
        score: response.data.compatibility_score,
        userName: currentUser.name
      });
      await sendChatMessage(resultsMessage);
      
      toast.success('Analysis complete! 💕');
      setSession(prev => ({
        ...prev,
        status: 'completed',
        compatibility_score: response.data.compatibility_score,
        compatibility_analysis: response.data.analysis
      }));
      setShowResults(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const generateBaby = async () => {
    try {
      setGeneratingBaby(true);
      const response = await axios.post(`${API}/photo-match/baby/${session.id}`, {}, {
        withCredentials: true,
        timeout: 180000
      });
      
      toast.success('Baby prediction ready! 👶');
      setSession(prev => ({
        ...prev,
        baby_image: response.data.baby_image
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Baby generation failed');
    } finally {
      setGeneratingBaby(false);
    }
  };

  const deleteSession = async () => {
    if (!window.confirm('Delete this session? All photos will be removed.')) return;
    
    try {
      await axios.delete(`${API}/photo-match/session/${session.id}`, {
        withCredentials: true
      });
      toast.success('Session deleted');
      setSession(null);
      setShowResults(false);
      setConsentGiven(false);
      setUploadMode(false);
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  // Open modal in upload mode (called from chat widget)
  const openForUpload = async () => {
    setShowModal(true);
    setUploadMode(true);
    await loadSession();
  };

  // Open modal in results mode (called from chat widget)
  const openForResults = async () => {
    setShowModal(true);
    await loadSession();
    setShowResults(true);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  const getScoreEmoji = (score) => {
    if (score >= 90) return '🔥💕';
    if (score >= 80) return '💖✨';
    if (score >= 70) return '💕';
    if (score >= 60) return '💛';
    if (score >= 50) return '🤔';
    return '😅';
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      );
    }

    // Show results if completed
    if (showResults && session?.compatibility_score) {
      return (
        <div className="space-y-6">
          <div className="text-center bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-6">
            <div className="text-6xl font-bold mb-2">
              <span className={getScoreColor(session.compatibility_score)}>
                {session.compatibility_score}%
              </span>
            </div>
            <p className="text-2xl">{getScoreEmoji(session.compatibility_score)}</p>
            <p className="text-gray-600 mt-2">Compatibility Score</p>
          </div>

          <div className="bg-white rounded-xl p-4 border">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
              <Sparkles className="w-4 h-4 mr-2 text-purple-500" />
              AI Analysis
            </h4>
            <p className="text-gray-600 text-sm whitespace-pre-wrap">
              {session.compatibility_analysis}
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-pink-50 rounded-xl p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
              <Baby className="w-4 h-4 mr-2 text-pink-500" />
              Baby Prediction
            </h4>
            
            {session.baby_image ? (
              <div className="text-center">
                <img 
                  src={`data:image/png;base64,${session.baby_image}`}
                  alt="Baby prediction"
                  className="rounded-xl mx-auto max-w-full shadow-lg"
                  style={{ maxHeight: '300px' }}
                />
                <p className="text-sm text-gray-500 mt-2">
                  AI-generated prediction for entertainment only 👶✨
                </p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-gray-600 mb-3">
                  Curious what your baby might look like? 👶
                </p>
                <Button
                  onClick={generateBaby}
                  disabled={generatingBaby}
                  className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                >
                  {generatingBaby ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating (up to 1 min)...
                    </>
                  ) : (
                    <>
                      <Baby className="w-4 h-4 mr-2" />
                      Generate Baby Prediction
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          <Button
            onClick={deleteSession}
            variant="outline"
            className="w-full text-red-500 border-red-200 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Session & Photos
          </Button>
        </div>
      );
    }

    // Upload mode - show photo upload interface
    if (uploadMode || (session && !session.my_photos_uploaded)) {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Upload className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Upload Your Photos</h3>
            <p className="text-gray-600 text-sm">
              Select up to 5 photos for AI compatibility analysis
            </p>
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoSelect}
              className="hidden"
              id="photo-upload-widget"
            />
            <label htmlFor="photo-upload-widget" className="cursor-pointer">
              <ImageIcon className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600 font-medium">Click to select photos</p>
              <p className="text-gray-400 text-sm">Up to 5 photos, max 10MB each</p>
            </label>
          </div>

          {selectedPhotos.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">{selectedPhotos.length} photo(s) selected</p>
              <div className="grid grid-cols-3 gap-2">
                {selectedPhotos.map((url, idx) => (
                  <div key={idx} className="relative aspect-square rounded-lg overflow-hidden">
                    <img src={url} alt={`Selected ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      onClick={() => setSelectedPhotos(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <Button
                onClick={submitPhotos}
                disabled={uploading}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Send Photos to {otherUser?.name}
              </Button>
            </div>
          )}
        </div>
      );
    }

    // Ready for analysis
    if (session?.status === 'both_uploaded') {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Ready to Discover! 🎉</h3>
            <p className="text-gray-600">
              Both of you have uploaded photos. Click below to see your compatibility!
            </p>
          </div>

          <Button
            onClick={runAnalysis}
            disabled={analyzing}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 py-6 text-lg"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Analyzing (up to 1 min)...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Discover Compatibility! ✨
              </>
            )}
          </Button>
        </div>
      );
    }

    // Waiting for partner
    if (session && session.my_photos_uploaded && !session.partner_photos_uploaded) {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <Loader2 className="w-10 h-10 text-white animate-spin" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Waiting for {otherUser?.name}</h3>
            <p className="text-gray-600">
              Your photos are uploaded! Waiting for {otherUser?.name} to upload theirs...
            </p>
          </div>
        </div>
      );
    }

    // Initial state - show consent and start
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Camera className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            Photo Compatibility Match
          </h3>
          <p className="text-gray-600">
            Upload photos with {otherUser?.name} to discover your compatibility and see what your baby might look like!
          </p>
        </div>

        <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl p-4">
          <h4 className="font-semibold text-gray-800 mb-2">How it works:</h4>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex items-center">
              <span className="w-6 h-6 rounded-full bg-pink-200 text-pink-700 flex items-center justify-center text-xs mr-2">1</span>
              Both of you upload photos
            </li>
            <li className="flex items-center">
              <span className="w-6 h-6 rounded-full bg-purple-200 text-purple-700 flex items-center justify-center text-xs mr-2">2</span>
              AI analyzes your compatibility (0-100%)
            </li>
            <li className="flex items-center">
              <span className="w-6 h-6 rounded-full bg-blue-200 text-blue-700 flex items-center justify-center text-xs mr-2">3</span>
              See what your baby might look like! 👶
            </li>
          </ul>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="consent"
              checked={consentGiven}
              onChange={(e) => setConsentGiven(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-gray-300 text-pink-500 focus:ring-pink-500"
            />
            <label htmlFor="consent" className="text-sm text-gray-700">
              <span className="font-semibold">I consent to sharing my photos</span>
              <br />
              <span className="text-gray-500">
                Photos will be analyzed by AI. Photos will be deleted when session ends.
              </span>
            </label>
          </div>
        </div>

        <Button
          onClick={startPhotoMatch}
          disabled={!consentGiven || loading}
          className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Heart className="w-4 h-4 mr-2" />
          )}
          Start Photo Match
        </Button>
      </div>
    );
  };

  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        variant="ghost"
        size="icon"
        className="rounded-full hover:bg-pink-100"
        title="Photo Compatibility Match"
      >
        <Camera className="w-5 h-5 text-pink-500" />
      </Button>

      <Dialog open={showModal} onOpenChange={handleModalClose}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Heart className="w-5 h-5 mr-2 text-pink-500" />
              Photo Match with {otherUser?.name}
            </DialogTitle>
            <DialogDescription>
              AI-powered compatibility analysis & baby prediction
            </DialogDescription>
          </DialogHeader>
          
          {renderContent()}
        </DialogContent>
      </Dialog>
    </>
  );
};

// Chat Widget Component - renders photo match messages in chat
export const PhotoMatchChatWidget = ({ message, isMe, currentUser, otherUser, matchId, onOpenPhotoMatch }) => {
  const parsed = parsePhotoMatchMessage(message.text);
  if (!parsed) return null;

  const { type, data } = parsed;

  // Uploaded message - waiting for other user
  if (type === PHOTO_MATCH_MESSAGE_TYPES.UPLOADED) {
    return (
      <div className="my-2">
        <div className={`rounded-xl p-4 shadow-lg max-w-xs ${
          isMe 
            ? 'bg-gradient-to-br from-purple-100 to-pink-100' 
            : 'bg-gradient-to-br from-blue-50 to-purple-50'
        }`}>
          <div className="flex items-center justify-center mb-3">
            <div className="text-4xl">📸💕</div>
          </div>
          <p className="text-center text-gray-700 text-sm font-semibold mb-2">
            {isMe ? 'You sent photos!' : `${data.userName} sent photos!`}
          </p>
          <p className="text-center text-gray-600 text-xs mb-3">
            {isMe 
              ? `Waiting for ${otherUser?.name} to upload their photos...`
              : 'Upload your photos to see your compatibility & what your baby might look like!'
            }
          </p>
          <div className="bg-white/80 rounded-lg p-3 mb-3">
            <p className="text-xs text-gray-500 text-center">
              🔮 AI Compatibility Match<br/>
              👶 Baby Prediction
            </p>
          </div>
          {!isMe && (
            <Button
              onClick={() => onOpenPhotoMatch('upload')}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold"
            >
              📷 Upload My Photos
            </Button>
          )}
          {isMe && (
            <p className="text-center text-xs text-gray-500">
              Waiting for their photos...
            </p>
          )}
        </div>
      </div>
    );
  }

  // Ready message - both uploaded
  if (type === PHOTO_MATCH_MESSAGE_TYPES.READY) {
    return (
      <div className="my-2">
        <div className="rounded-xl p-4 shadow-lg max-w-xs bg-gradient-to-br from-green-50 to-blue-50">
          <div className="flex items-center justify-center mb-3">
            <div className="text-4xl">✨🎉</div>
          </div>
          <p className="text-center text-gray-700 text-sm font-semibold mb-2">
            Photos Ready!
          </p>
          <p className="text-center text-gray-600 text-xs mb-3">
            Both of you have uploaded photos! Click to discover your compatibility!
          </p>
          <Button
            onClick={() => onOpenPhotoMatch('results')}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            See Results! 🔮
          </Button>
        </div>
      </div>
    );
  }

  // Results message
  if (type === PHOTO_MATCH_MESSAGE_TYPES.RESULTS) {
    return (
      <div className="my-2">
        <div className="rounded-xl p-4 shadow-lg max-w-xs bg-gradient-to-br from-pink-50 to-purple-50">
          <div className="flex items-center justify-center mb-3">
            <div className="text-4xl">💕</div>
          </div>
          <p className="text-center text-gray-700 text-sm font-semibold mb-2">
            Compatibility Results!
          </p>
          <div className="bg-white rounded-lg py-3 px-4 mb-3 text-center">
            <span className="text-3xl font-bold text-purple-600">{data.score}%</span>
            <span className="text-gray-600 text-sm ml-2">compatible</span>
          </div>
          <Button
            onClick={() => onOpenPhotoMatch('results')}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold"
          >
            <Baby className="w-4 h-4 mr-2" />
            View Full Results & Baby 👶
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default PhotoMatchWidget;
